import {
  getActivitySessionId,
  getSessionDeviceClass,
} from "@/services/activity/portalActivitySession";

export const ActivityCategory = {
  page: "Page",
  click: "Click",
  cms: "Cms",
  error: "Error",
} as const;

export type PortalActivityCategory =
  (typeof ActivityCategory)[keyof typeof ActivityCategory];

// Which door the server should take an event through, decided by how much the
// session had proved when the event happened, not when it is sent.
export type ActivityChannel = "account" | "session" | "anonymous";

export interface PortalActivityEvent {
  occurred: string;
  category: PortalActivityCategory;
  action: string;
  target: string;
  reference: string;
}

// The server dates an event by its age, measured on this device's clock at both ends,
// because the device clock itself may be set wrong and an event can wait a while to go.
export interface PortalActivityReport extends PortalActivityEvent {
  ageSeconds: number;
}

// The session and device are those the event happened in, so one held across a sign in
// still goes under the session it belongs to.
export interface QueuedPortalActivity {
  channel: ActivityChannel;
  customerId: string;
  entityId: string;
  awaitingAccount: boolean;
  sessionId: string;
  device: string;
  event: PortalActivityEvent;
}

export interface ActivityIdentity {
  isAuthenticated: boolean;
  customerId: string;
  entityId: string;
  // Signed in, with the grower yet to choose an account.
  awaitingAccount: boolean;
}

export interface ActivityGroup {
  channel: ActivityChannel;
  customerId: string;
  entityId: string;
  sessionId: string;
  device: string;
  events: PortalActivityEvent[];
}

export interface PortalActivityBatch {
  channel: ActivityChannel;
  customerId: string;
  entityId: string;
  events: PortalActivityReport[];
  sessionId: string;
  device: string;
}

// Thrown by a send the server answered with a refusal, so certainly stored none of it.
// Any other failure, a timeout above all, may have been stored.
// Why a signed in batch was refused, where that changes what becomes of it: the refusal
// signed the tab out (sessionEnded), or the token could not be renewed for an outage, so
// the events were never judged (renewalFailed).
export type PortalActivityRefusal = "sessionEnded" | "renewalFailed";

export class PortalActivityRefusedError extends Error {
  constructor(
    public readonly status: number,
    public readonly outcome?: PortalActivityRefusal
  ) {
    super(`Portal activity refused: ${status}`);
    this.name = "PortalActivityRefusedError";
  }
}

export interface PortalActivityTrackerOptions {
  isEnabled: () => boolean;
  getIdentity: () => ActivityIdentity;
  // False while this device's clock says the signed in token has expired. The clock can
  // be wrong, so signed in events are then held rather than sent to be thrown away.
  canSendSignedIn?: () => boolean;
  // Sends held signed in events through the portal's own requests, where only the
  // server's 401 refreshes the token. The device clock alone never does.
  sendHeld?: (batch: PortalActivityBatch) => Promise<void>;
  send: (batch: PortalActivityBatch, keepalive: boolean) => Promise<void>;
  flushIntervalMilliseconds?: number;
  maximumBatch?: number;
}

export type ActivityFlushReason =
  | "routine"
  | "accountChange"
  | "sessionChange"
  | "pageHidden";

const DEFAULT_FLUSH_INTERVAL = 15000;
const DEFAULT_MAXIMUM_BATCH = 50;
const ANONYMOUS_MAXIMUM_BATCH = 10;
const MAXIMUM_QUEUE = 500;
const ACCOUNT_CHOICE_WAIT = 5 * 60_000;
const FIRST_HELD_RETRY = 60_000;
const LAST_HELD_RETRY = 15 * 60_000;
const FIRST_ANONYMOUS_PAUSE = 60_000;
const LAST_ANONYMOUS_PAUSE = 15 * 60_000;

// The server takes only page views and marketing content from someone not yet signed in.
const REPORTABLE_BEFORE_SIGN_IN: readonly PortalActivityCategory[] = [
  ActivityCategory.page,
  ActivityCategory.cms,
];

// Only what the grower did sends held events, which may refresh the token, so tracking
// alone never keeps an idle session signed in past its inactivity limit.
const SENDS_HELD: readonly PortalActivityCategory[] = [
  ActivityCategory.page,
  ActivityCategory.click,
];

let options: PortalActivityTrackerOptions | undefined;
let queue: QueuedPortalActivity[] = [];
let flushTimer: ReturnType<typeof setInterval> | undefined;
let sending: Promise<void> | undefined;
let pageListenersInstalled = false;
let heldSentAt = 0;
let nextHeldSendAt = 0;
let heldRetry = FIRST_HELD_RETRY;
let tokenRefused = false;
let anonymousPausedUntil = 0;
let anonymousPause = FIRST_ANONYMOUS_PAUSE;
const droppedListeners: ((events: PortalActivityEvent[]) => void)[] = [];

function channelFor(identity: ActivityIdentity): ActivityChannel {
  if (!identity.isAuthenticated) {
    return "anonymous";
  }

  return identity.customerId ? "account" : "session";
}

function clip(value: string | undefined, length: number): string {
  return (value ?? "").trim().slice(0, length);
}

function lastPageIndex(queued: QueuedPortalActivity[]): number {
  for (let index = queued.length - 1; index >= 0; index--) {
    if (queued[index].event.category === ActivityCategory.page) {
      return index;
    }
  }

  return -1;
}

export function onActivityDropped(listener: (events: PortalActivityEvent[]) => void) {
  droppedListeners.push(listener);

  return () => {
    const index = droppedListeners.indexOf(listener);

    if (index >= 0) {
      droppedListeners.splice(index, 1);
    }
  };
}

function notifyDropped(events: PortalActivityEvent[]) {
  if (events.length === 0) {
    return;
  }

  for (const listener of droppedListeners) {
    try {
      listener(events);
    } catch {
      // one listener failing must not keep the drop from the others
    }
  }
}

// Nothing more is sent while a send is outstanding, so a slow API must not grow this without limit.
function keepQueueBounded() {
  if (queue.length > MAXIMUM_QUEUE) {
    notifyDropped(queue.slice(0, queue.length - MAXIMUM_QUEUE).map((x) => x.event));
    queue = queue.slice(queue.length - MAXIMUM_QUEUE);
  }
}

// Usage tracking must never interrupt the grower, so every failure here is swallowed.
// True once the event is queued to be sent.
export function trackActivity(
  category: PortalActivityCategory,
  action: string,
  target = "",
  reference = ""
): boolean {
  try {
    if (!options || !options.isEnabled() || !action) {
      return false;
    }

    const identity = options.getIdentity();
    const channel = channelFor(identity);

    if (channel === "anonymous" && !REPORTABLE_BEFORE_SIGN_IN.includes(category)) {
      return false;
    }

    queue.push({
      channel,
      customerId: identity.customerId,
      entityId: identity.entityId,
      awaitingAccount: identity.awaitingAccount === true,
      sessionId: getActivitySessionId(),
      device: getSessionDeviceClass(),
      event: {
        occurred: new Date().toISOString(),
        category,
        action: clip(action, 100),
        target: clip(target, 100),
        reference: clip(reference, 50),
      },
    });

    keepQueueBounded();

    if (queue.length >= (options.maximumBatch ?? DEFAULT_MAXIMUM_BATCH)) {
      void flushActivity();
    }

    return true;
  } catch {
    return false;
  }
}

// Grouped by route name, the key clicks refer to; the name the grower sees on screen
// goes alongside it for the dashboard to show.
export function trackPageView(
  routeName: string,
  meta: { displayName?: unknown; title?: unknown }
) {
  const displayName = meta.displayName || meta.title;

  trackActivity(
    ActivityCategory.page,
    "View",
    routeName,
    typeof displayName === "string" ? displayName : ""
  );
}

export function groupForSending(
  queued: QueuedPortalActivity[],
  maximumBatch: number
): ActivityGroup[] {
  const batches: ActivityGroup[] = [];

  for (const item of queued) {
    const limit =
      item.channel === "anonymous"
        ? Math.min(maximumBatch, ANONYMOUS_MAXIMUM_BATCH)
        : maximumBatch;
    let batch = batches.find(
      (x) =>
        x.channel === item.channel &&
        x.customerId === item.customerId &&
        x.entityId === item.entityId &&
        x.sessionId === item.sessionId &&
        x.device === item.device &&
        x.events.length < limit
    );

    if (!batch) {
      batch = {
        channel: item.channel,
        customerId: item.customerId,
        entityId: item.entityId,
        sessionId: item.sessionId,
        device: item.device,
        events: [],
      };
      batches.push(batch);
    }

    batch.events.push(item.event);
  }

  return batches;
}

// The server checks the account on every request and ends the session if the login
// no longer holds it, so an event is only ever sent under the account open now. One
// queued under an account since closed or removed goes as the login's alone, and
// anything queued while signed in is dropped once the grower has signed out. The page
// the grower was on when they chose an account is that account's view.
export function readdressForCurrentSession(
  queued: QueuedPortalActivity[],
  current: ActivityIdentity
): QueuedPortalActivity[] {
  const lastPage = lastPageIndex(queued);

  return queued
    .map((x, index) =>
      index === lastPage && x.awaitingAccount && current.isAuthenticated && current.customerId
        ? {
            ...x,
            channel: "account" as const,
            customerId: current.customerId,
            entityId: current.entityId,
            awaitingAccount: false,
          }
        : x
    )
    .filter((x) => x.channel === "anonymous" || current.isAuthenticated)
    .map((x) =>
      x.channel === "account" &&
      (x.customerId !== current.customerId || x.entityId !== current.entityId)
        ? { ...x, channel: "session" as const, customerId: "", entityId: "" }
        : x
    );
}

// While the grower is still choosing an account, the page they are on waits to go under
// the account they choose. A login that never chooses one sends it once they move on,
// hide the tab or sign out, or after a few minutes.
function findPageAwaitingAccount(
  queued: QueuedPortalActivity[],
  current: ActivityIdentity
): QueuedPortalActivity | undefined {
  const page = current.awaitingAccount ? queued[lastPageIndex(queued)] : undefined;

  return page?.awaitingAccount &&
    Date.now() - Date.parse(page.event.occurred) < ACCOUNT_CHOICE_WAIT
    ? page
    : undefined;
}

// Signed in events held because this device's clock says the token has expired go to
// the server to judge once the grower does something, so none are lost and a token the
// server still takes is never refreshed: only its 401 refreshes one. Sending again needs
// something the grower has done since, so a grower who walks away is not kept signed in,
// and it backs off while the sends fail.
function shouldSendHeld(signedIn: QueuedPortalActivity[]): boolean {
  return (
    options?.sendHeld !== undefined &&
    Date.now() >= nextHeldSendAt &&
    signedIn.some(
      (x) =>
        SENDS_HELD.includes(x.event.category) &&
        Date.parse(x.event.occurred) > heldSentAt
    )
  );
}

function noteHeldSent() {
  heldSentAt = Date.now();
  nextHeldSendAt = heldSentAt + heldRetry;
  heldRetry = Math.min(heldRetry * 2, LAST_HELD_RETRY);
}

function noteSignedInAccepted() {
  nextHeldSendAt = 0;
  heldRetry = FIRST_HELD_RETRY;
  tokenRefused = false;
}

// The sign in page's door is throttled per caller, and behind a proxy every grower can
// share one caller. After a refusal this tab waits before trying it again, doubling the
// wait while refusals continue, so impressions it holds meanwhile are sent once rather
// than re-sent into the throttle on every slide.
function noteAnonymousSend(accepted: boolean) {
  if (accepted) {
    anonymousPause = FIRST_ANONYMOUS_PAUSE;
    return;
  }

  anonymousPausedUntil = Date.now() + anonymousPause;
  anonymousPause = Math.min(anonymousPause * 2, LAST_ANONYMOUS_PAUSE);
}

function toReport(event: PortalActivityEvent, sentAt: number): PortalActivityReport {
  const occurredAt = Date.parse(event.occurred);

  return {
    ...event,
    ageSeconds: Number.isFinite(occurredAt)
      ? Math.max(0, Math.floor((sentAt - occurredAt) / 1000))
      : 0,
  };
}

// A routine flush waits for the last send to finish, so a slow API never has sends
// piling up. A flush because the session or account is changing hands, or the page is
// going away, never waits: what is queued must go now, under the session it belongs
// to. Only the page going away is sent keepalive, because the browser caps keepalive
// requests in flight and silently drops any past the cap.
export async function flushActivity(reason: ActivityFlushReason = "routine") {
  if (!options || queue.length === 0 || (reason === "routine" && sending)) {
    return;
  }

  const pending = queue;
  queue = [];

  try {
    const identity = options.getIdentity();
    const readdressed = readdressForCurrentSession(pending, identity);
    // A token the server has refused stays in doubt, whatever this device's clock says,
    // until the server takes a held send.
    const canSendSignedIn = (options.canSendSignedIn?.() ?? true) && !tokenRefused;
    const pageAwaitingAccount =
      reason === "routine" || reason === "accountChange"
        ? findPageAwaitingAccount(readdressed, identity)
        : undefined;
    const anonymousPaused = Date.now() < anonymousPausedUntil;

    if (canSendSignedIn) {
      noteSignedInAccepted();
    }

    // Only on a routine flush: never as the page goes away or the session changes hands.
    const sendingHeld =
      !canSendSignedIn &&
      reason === "routine" &&
      shouldSendHeld(
        readdressed.filter((x) => x !== pageAwaitingAccount && x.channel !== "anonymous")
      );
    const ready = readdressed.filter((x) =>
      x === pageAwaitingAccount
        ? false
        : x.channel === "anonymous"
          ? !anonymousPaused
          : canSendSignedIn || sendingHeld
    );
    const readyItems = new Set(ready);
    const held = readdressed.filter((x) => !readyItems.has(x));
    const kept = new Set(readdressed.map((x) => x.event));

    notifyDropped(pending.filter((x) => !kept.has(x.event)).map((x) => x.event));

    queue = held.concat(queue);
    keepQueueBounded();

    if (sendingHeld) {
      noteHeldSent();
    }

    const batches = groupForSending(
      ready,
      options.maximumBatch ?? DEFAULT_MAXIMUM_BATCH
    );

    // Every request is started before any is awaited, so a flush begun just before
    // sign out still goes with the session it was recorded under.
    const sentAt = Date.now();
    const keepalive = reason === "pageHidden";
    const sendHeld = sendingHeld ? options.sendHeld : undefined;
    const send = Promise.allSettled(
      batches.map((batch) => {
        const report = { ...batch, events: batch.events.map((event) => toReport(event, sentAt)) };

        return sendHeld && batch.channel !== "anonymous"
          ? sendHeld(report)
          : options!.send(report, keepalive);
      })
    ).then((results) =>
      results.forEach((result, index) => {
        const batch = batches[index];
        const refused =
          result.status === "rejected" && result.reason instanceof PortalActivityRefusedError
            ? (result.reason as PortalActivityRefusedError)
            : undefined;
        const refusal = refused?.status;
        // the tab has changed hands since the batch went: its answer, taken or refused, says nothing of the new login's token
        const handedOver = batch.sessionId !== getActivitySessionId();

        if (batch.channel === "anonymous") {
          noteAnonymousSend(result.status === "fulfilled");
        } else if (sendHeld && result.status === "fulfilled" && !handedOver) {
          noteSignedInAccepted();
        } else if (sendHeld && refused?.outcome === "renewalFailed" && !handedOver) {
          // Never judged: kept for a held send once the token can be renewed, still backing off.
          queue = readdressed.filter((x) => batch.events.includes(x.event)).concat(queue);
          keepQueueBounded();
          return;
        } else if (
          !sendHeld &&
          refusal === 401 &&
          options?.sendHeld &&
          refused?.outcome !== "sessionEnded" &&
          !handedOver
        ) {
          // Expired on the server's clock though not on this device's: held for the server
          // to judge, as if the device's clock had said so, rather than lost.
          tokenRefused = true;
          queue = readdressed.filter((x) => batch.events.includes(x.event)).concat(queue);
          keepQueueBounded();
          return;
        }

        // Only a refusal is certainly lost; a send that timed out may have been stored.
        if (refusal !== undefined) {
          notifyDropped(batch.events);
        }
      })
    );

    sending = send;
    await send;

    if (sending === send) {
      sending = undefined;
    }
  } catch {
    // dropped: an unsent page view is not worth retrying into a failing API
  }
}

export function installPortalActivityTracking(
  trackerOptions: PortalActivityTrackerOptions
) {
  options = trackerOptions;

  if (flushTimer) {
    clearInterval(flushTimer);
  }

  flushTimer = setInterval(
    () => void flushActivity(),
    trackerOptions.flushIntervalMilliseconds ?? DEFAULT_FLUSH_INTERVAL
  );

  if (pageListenersInstalled) {
    return;
  }

  pageListenersInstalled = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushActivity("pageHidden");
    }
  });

  window.addEventListener("pagehide", () => void flushActivity("pageHidden"));
}

// A token refused for the last login says nothing about the next one's.
export function forgetHeldSends() {
  heldSentAt = 0;
  nextHeldSendAt = 0;
  heldRetry = FIRST_HELD_RETRY;
  tokenRefused = false;
}

export function resetPortalActivityTrackingForTests() {
  if (flushTimer) {
    clearInterval(flushTimer);
  }

  options = undefined;
  queue = [];
  flushTimer = undefined;
  sending = undefined;
  heldSentAt = 0;
  nextHeldSendAt = 0;
  heldRetry = FIRST_HELD_RETRY;
  tokenRefused = false;
  anonymousPausedUntil = 0;
  anonymousPause = FIRST_ANONYMOUS_PAUSE;
}

export function getQueuedActivityForTests() {
  return [...queue];
}

// The portal API's own path words. Every other segment is a record key, a contract
// number or a ship-to or buyer code such as HOME, and is collapsed, so errors group by
// endpoint and no grower's codes are recorded. A new endpoint's words belong here; one
// left out is only collapsed too.
const API_ROUTE_WORDS = new Set([
  "accountAudit", "accountSelection", "accountsReceivable", "activity", "addresses",
  "adjustments", "agriFinancePrograms", "anonymous", "api", "applications", "audit",
  "auth", "autoPayment", "autoPaymentSetup", "balance", "balances",
  "bidsBySiteAndCommodity", "bidsBySiteByCommodity", "bookings", "budgetBilling",
  "buyers", "categories", "checkout", "checks", "chemical", "clientToken",
  "commodities", "contracts", "contractSubTypes", "contractTypes", "copy",
  "creditActivity", "creditCard", "customer", "customerPortal", "customerSettings",
  "dateSelectionSettings", "deliveries", "discounts", "divisions",
  "energyAssistance", "entity", "exchangePrices", "export", "fieldAlytics", "fields",
  "filter", "forgotPassword", "forgotUsername", "fortis", "fuelPurchaseSummary",
  "futuresMonthDeliveryPeriodSite", "futuresPrice", "generalLedger", "grades",
  "grainBank", "inventory", "invoice", "invoices", "isOilEnergyCustomer", "items",
  "layout", "leaseAgreements", "legal-terms", "licenses", "list", "login",
  "loginActivity", "lookup", "manager", "offers", "oilEnergy", "orders", "parameter",
  "parameterCaseOfDispute", "parameterContactInfo", "parameterSettings",
  "parameterTermsAndConditions", "patronage", "payment", "paymentCreation",
  "pending", "pendingPaymentSettings", "periods", "permits", "portalOrdering",
  "prepaids", "pricings", "profile", "proofOfYield", "purchases", "purchaseSummary",
  "receipts", "refreshToken", "reorder", "report", "resetPassword",
  "resetPasswordToken", "sales", "salesContracts", "salesOverview", "search",
  "session", "settings", "settlements", "shipTo", "shipTos", "sign", "sites", "soft",
  "sources", "splits", "statement", "statements", "submit", "summaries", "summary",
  "tanks", "tax", "tech", "tickets", "transactions", "upcomingDeliveries",
  "userDashboardWidgets", "userFavorites", "userProfileBasedSecurity", "usStates",
  "void", "webLogin", "webMap",
]);

function isRouteWord(segment: string): boolean {
  return (
    segment === "" ||
    /^v\d+$/.test(segment) ||
    API_ROUTE_WORDS.has(segment)
  );
}

export function toEndpointTemplate(url: string | undefined): string {
  if (!url) {
    return "";
  }

  return url
    .split("?")[0]
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/?api\/customerPortal\/v\d+\//i, "")
    .split("/")
    .map((segment) => (isRouteWord(segment) ? segment : "{id}"))
    .join("/");
}

// A label such as "Ship-To Locations" as one camelCase word, "shipToLocations", for a
// click name built from text: the Public API drops a name holding a space or punctuation.
export function toTrackWord(label: string): string {
  return label
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => word)
    .map((word, index) =>
      (index === 0 ? word[0].toLowerCase() : word[0].toUpperCase()) + word.slice(1)
    )
    .join("");
}
